import {
  Provider,
  Root,
  Trigger,
  Portal,
  Content,
  Arrow,
} from '@radix-ui/react-tooltip';
import React from 'react';
import { useSelector } from 'react-redux';

import { selectAuthFeatureFlags } from '../../../../../auth/AuthSelectors';
import { getConversationalSearchUnavailableMessage } from './conversationalSearchMessaging';

const NAV_BAR_HEIGHT = 80;

const AICtaTooltipContent = ({ enabled }: { enabled: boolean }) => (
  <div className="flex items-center gap-3 p-3 pl-4">
    <img
      height={170}
      width={170}
      src="/assets/common/images/conversational-tooltip.webp"
      srcSet="/assets/common/images/conversational-tooltip.webp, /assets/common/images/conversational-tooltip@2x.webp 2x"
      alt="Conversational search preview"
      className="flex-shrink-0 rounded border border-white object-cover"
    />
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 pb-1">
        <span className="text-base font-semibold text-white">
          {enabled ? 'Conversational Search Enabled!' : 'Conversational Search'}
        </span>
        {!enabled && (
          <span className="rounded bg-gray-700 px-1.5 py-0.5 text-xs font-medium text-white">
            NEW
          </span>
        )}
      </div>
      <p className="text-sm leading-5 text-gray-400">
        Find content by describing your needs with natural language and refine
        your search through chat.
      </p>
    </div>
  </div>
);

// Shown when the auto-on-hover feature is enabled: a stripped-down, text-only
// hint with no image, title, or badge — centered white copy.
const AutoOnHintTooltipContent = () => (
  <div className="flex items-center justify-center p-3 w-64">
    <p className="text-center text-base font-semibold leading-7 text-white">
      Search and refine using natural language
    </p>
  </div>
);

const StandardSearchTooltipContent = ({
  onDismiss,
}: {
  onDismiss: () => void;
}) => (
  <div className="flex items-center gap-3 p-3 pl-4 w-48">
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 pb-1">
        <span className="text-base font-semibold text-white">
          Standard Search
        </span>
      </div>
      <p className="text-sm leading-5 text-gray-400">
        Switch back to standard search anytime.
      </p>
      <div className="mt-2 flex items-center gap-2.5">
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm font-normal text-white hover:underline cursor-pointer bg-transparent border-0 p-0"
        >
          Switch Back
        </button>
      </div>
    </div>
  </div>
);

const DisabledForContentTypeTooltipContent = () => {
  const { showConversationalSearchAudio } = useSelector(selectAuthFeatureFlags);
  return (
    <div className="flex items-center gap-3 p-3 pl-4">
      <div className="flex flex-col">
        <div className="flex items-center gap-2.5 pb-1">
          <span className="text-base text-white">
            {getConversationalSearchUnavailableMessage(
              showConversationalSearchAudio ?? false
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

interface ConversationalSearchTooltipProps {
  disabledForContentType?: boolean;
  enabled: boolean;
  // True when the auto-on-hover feature is configured on (delay > 0); surfaces the text-only hint.
  autoOnEnabled?: boolean;
  setEnabled: (enabled: boolean) => void;
  trigger: React.ReactElement;
}

export default function ConversationalSearchTooltip({
  disabledForContentType,
  enabled,
  autoOnEnabled,
  setEnabled,
  trigger,
}: ConversationalSearchTooltipProps) {
  const [hovered, setHovered] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Capture `enabled` at hover start so toggling mid-hover doesn't swap content
  const [enabledAtHover, setEnabledAtHover] = React.useState(false);

  const cancelClose = (): void => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (): void => {
    cancelClose();
    if (!hovered) {
      setEnabledAtHover(enabled);
    }
    setHovered(true);
  };

  const handleMouseLeave = (): void => {
    // add a slight delay before hiding to allow users to move their mouse into the tooltip without it disappearing
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => setHovered(false), 150);
  };

  // cleanup timeout etc on unmount
  React.useEffect(() => cancelClose, []);

  // We need to instantiate a new Provider here to get around BuilderBlock's use of a separate React tree.
  // Builder-rendered tooltips can't access the state/context of the main app (Providers live in the root layout)
  return (
    <Provider>
      <Root
        delayDuration={0}
        open={hovered}
        onOpenChange={(open) => !open && !hovered && setHovered(false)}
      >
        <Trigger
          asChild
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {trigger}
        </Trigger>
        <Portal>
          <Content
            collisionPadding={{ top: NAV_BAR_HEIGHT }}
            sideOffset={8}
            className="z-50 max-w-md rounded-xl bg-gray-950 shadow-lg"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {disabledForContentType ? (
              <DisabledForContentTypeTooltipContent />
            ) : enabledAtHover ? (
              <StandardSearchTooltipContent
                onDismiss={() => setEnabled(false)}
              />
            ) : autoOnEnabled ? (
              <AutoOnHintTooltipContent />
            ) : (
              <AICtaTooltipContent enabled={enabled} />
            )}
            <Arrow
              style={{
                fill: 'rgb(26, 28, 30)',
                clipPath: 'inset(0 -10px -10px -10px)',
              }}
            />
          </Content>
        </Portal>
      </Root>
    </Provider>
  );
}
